import internalNotesService from '@/services/internalNotesService';
import axios from 'axios';
import { CLOUD_SERVICE_BASE_URL } from '@/utils/const';
import { attachTokenRefresh } from '@/utils/authInterceptors';

// Create axios instance for customer list API
const customerListService = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === "production"
    ? "/portal/order/"
    : "/portal/order/staging/"
  )
});

customerListService.interceptors.request.use(
  async function(config) {
    let token = sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

attachTokenRefresh(customerListService);

/**
 * Get all internal notes for a patient
 * @param {string} patientId - patient ID
 * @param {Object} params - query parameters
 * @param {number} params.page - page number (default: 1)
 * @param {number} params.limit - records per page (default: 20)
 * @param {string} params.search - search keyword
 * @param {string} params.date_from - start date (ISO 8601)
 * @param {string} params.date_to - end date (ISO 8601)
 * @param {string} params.sort_order - 'asc' or 'desc' (default: 'desc')
 * @returns {Promise} API response with { data: [...], meta: {...} }
 */
export const getInternalNotesApi = async (patientId, params = {}) => {
  try {
    console.log('Fetching internal notes for patient:', patientId, 'with params:', params);
    const response = await internalNotesService.get(`/patients/${patientId}/internal_notes`, {
      params
    });
    console.log('Internal notes fetched successfully:', response.data);
    // Backend returns { data: [...], meta: {...} }
    return response.data;
  } catch (error) {
    console.error('Failed to fetch internal notes:', error);
    throw error;
  }
};

/**
 * Create a new internal note
 * @param {string} patientId - patient ID
 * @param {Object} data - note data
 * @param {string} data.content - note content
 * @returns {Promise} API response
 */
export const createInternalNoteApi = async (patientId, data) => {
  try {
    console.log('Creating internal note for patient:', patientId, 'with data:', data);
    const response = await internalNotesService.post(`/patients/${patientId}/internal_notes`, data);
    console.log('Internal note created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to create internal note:', error);
    throw error;
  }
};

/**
 * Update an existing internal note
 * @param {string} patientId - patient ID
 * @param {string} noteId - note ID
 * @param {Object} data - note data
 * @param {string} data.content - updated note content
 * @returns {Promise} API response
 */
export const updateInternalNoteApi = async (patientId, noteId, data) => {
  try {
    console.log('Updating internal note:', noteId, 'for patient:', patientId, 'with data:', data);
    const response = await internalNotesService.put(`/patients/${patientId}/internal_notes/${noteId}`, data);
    console.log('Internal note updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to update internal note:', error);
    throw error;
  }
};

/**
 * Archive an internal note
 * @param {string} patientId - patient ID
 * @param {string} noteId - note ID
 * @returns {Promise} API response
 */
export const archiveInternalNoteApi = async (patientId, noteId) => {
  try {
    console.log('Archiving internal note:', noteId, 'for patient:', patientId);
    const response = await internalNotesService.post(`/patients/${patientId}/internal_notes/${noteId}/archive`);
    console.log('Internal note archived successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to archive internal note:', error);
    throw error;
  }
};

/**
 * Get all replies for a note
 * @param {string} noteId - note ID
 * @param {Object} params - query parameters
 * @param {number} params.page - page number (default: 1)
 * @param {number} params.limit - records per page (default: 20)
 * @param {boolean} params.include_nested - include nested reply structure (default: false)
 * @returns {Promise} API response with { data: [...], meta: {...} }
 */
export const getRepliesApi = async (noteId, params = {}) => {
  try {
    console.log('Fetching replies for note:', noteId, 'with params:', params);
    const response = await internalNotesService.get(`/internal_notes/${noteId}/replies`, {
      params
    });
    console.log('Replies fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch replies:', error);
    throw error;
  }
};

/**
 * Create a reply to a note
 * @param {string} noteId - note ID
 * @param {Object} data - reply data
 * @param {string} data.content - reply content
 * @param {string} data.parent_reply_id - optional parent reply ID for nested replies
 * @returns {Promise} API response
 */
export const createReplyApi = async (noteId, data) => {
  try {
    console.log('Creating reply for note:', noteId, 'with data:', data);
    const response = await internalNotesService.post(`/internal_notes/${noteId}/replies`, data);
    console.log('Reply created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to create reply:', error);
    throw error;
  }
};

/**
 * Mark a note as read
 * @param {string} noteId - note ID
 * @returns {Promise} API response
 */
export const markNoteAsReadApi = async (noteId) => {
  try {
    console.log('Marking note as read:', noteId);
    const response = await internalNotesService.post(`/internal_notes/${noteId}/mark_read`);
    console.log('Note marked as read successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to mark note as read:', error);
    throw error;
  }
};

/**
 * Get unread internal notes where the current doctor has been mentioned
 * @param {Object} params - query parameters
 * @param {string} params.patient_id - optional patient ID filter
 * @param {number} params.page - page number (default: 1)
 * @param {number} params.limit - items per page (default: 20, max: 100)
 * @returns {Promise} API response with { data: [...], meta: {...} }
 */
export const getInternalNotesMentionsApi = async (params = {}) => {
  const response = await internalNotesService.get('/internal_notes/mentions', { params });
  return response.data;
};

/**
 * Get list of all customers for mention functionality
 * @returns {Promise} API response with customer_id -> name mapping
 * Example: [{ "999997": "Vibrant IT3333" }, { "999998": "Kris Legaspi" }]
 */
export const getJwtTokenCustomerList = async () => {
  try {
    console.log('Fetching JWT token customer list...');
    const response = await customerListService.get('/customer/getJwtTokenCustomerList');
    console.log('Customer list fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch customer list:', error);
    throw error;
  }
};
